import { Dispatch } from "@reduxjs/toolkit";
import {
  resetState,
  changeLoginModalTitle,
  showLoginModal,
  hideLoginModal,
  onEmailChanged,
  showOtp,
  generateOtpSuccess,
  generateOtpError,
  generateOtp,
  onOTPChanged,
  verifyOtp,
  verifyOtpError,
  verifyOtpSuccess,
  hideOtp,
  onGoogleOneTapLoaded,
  onPhoneChanged,
  onCountryCodeChanged,
  onPhoneOTPChanged,
  showPhoneOtp,
  hidePhoneOtp,
  setIsAcceptTC,
} from "./loginReducer";
import generateOtpRequest, {
  generateMobileOtpRequest,
  verifyMobileOtpRequest,
  verifyOtpRequest,
} from "./components/helper";
import { updateUserData } from "@App/appReducer";
import { openToast } from "@Components/ToastNotification/toastReducer";
import { setLocalStorageData } from "@Components/LeadFormModal/data/leadFormReducer";
import loginSuccess from "./helpers/loginSuccess";
import { isApp } from "hooks/useIsApp";
import { sendWebViewEvent } from "@Utils/webviewEvents";
import { WEB_VIEW_APP_EVENTS } from "@Utils/webApputils/analyticEvents";

class LoginDispatcher {
  private dispatch;

  constructor(dispatch: Dispatch) {
    this.dispatch = dispatch;
  }

  changeLoginModalTitle(payload: any) {
    this.dispatch(changeLoginModalTitle(payload));
  }

  showLoginModal(payload: any) {
    if (isApp()) {
      sendWebViewEvent(WEB_VIEW_APP_EVENTS.LOGIN_CLICKED, { screenName: payload.screenName });
    } else {
      this.dispatch(showLoginModal(payload));
    }
  }

  hideLoginModal() {
    this.dispatch(hideLoginModal());
  }

  onEmailChanged(email: string) {
    this.dispatch(onEmailChanged(email));
  }

  onPhoneChanged(phone: string) {
    this.dispatch(onPhoneChanged(phone));
  }

  onCountryCodeChanged(code: string) {
    this.dispatch(onCountryCodeChanged(code));
  }

  onOTPChanged(otp: string) {
    this.dispatch(onOTPChanged(otp));
  }

  onPhoneOTPChanged(otp: string) {
    this.dispatch(onPhoneOTPChanged(otp));
  }

  showOtp() {
    this.dispatch(showOtp());
  }

  hideOtp() {
    this.dispatch(hideOtp());
  }

  showPhoneOtp() {
    this.dispatch(showPhoneOtp());
  }

  hidePhoneOtp() {
    this.dispatch(hidePhoneOtp());
  }

  onGoogleOneTapLoaded() {
    this.dispatch(onGoogleOneTapLoaded());
  }

  setIsAcceptTC(payload: boolean) {
    this.dispatch(setIsAcceptTC(payload));
  }

  resetState() {
    this.dispatch(resetState());
  }

  generateOtpError(error: string) {
    this.dispatch(generateOtpError(error));
  }

  async generateOtp(
    email: string,
    token: unknown | string = "",
    with_visual_challenge = false,
    isPartnerLogin = false,
  ) {
    this.dispatch(generateOtp(with_visual_challenge ? "v2" : "v3"));
    const { data, error } = await generateOtpRequest(
      email,
      token,
      with_visual_challenge,
      isPartnerLogin,
    );
    if (data) {
      this.dispatch(generateOtpSuccess());
    } else {
      this.dispatch(generateOtpError(error));
    }
  }

  async generateMobileOtp(
    phone: string,
    token: unknown | string = "",
    with_visual_challenge = false,
  ) {
    this.dispatch(generateOtp(""));
    const { data, error } = await generateMobileOtpRequest(phone, token, with_visual_challenge);
    if (data) {
      this.dispatch(generateOtpSuccess(data?.data?.session_uuid));
    } else {
      this.dispatch(generateOtpError(error));
    }
  }

  async onOTPContinue(email: string, otp: string) {
    this.dispatch(verifyOtp());
    const { data, error } = await verifyOtpRequest(email, otp);
    if (data) {
      loginSuccess(data?.data, "email_otp", "");
      this.dispatch(
        updateUserData({
          status: true,
          data: data?.data,
        }),
      );
      this.dispatch(setLocalStorageData());
      this.dispatch(
        openToast({
          title: "Login success",
          message: "You are successfully logged in",
        }),
      );
      this.dispatch(verifyOtpSuccess());
    } else {
      this.dispatch(verifyOtpError(error));
    }
  }

  async onMobileOTPContinue(mobile: string, otp: string, sessionId: string) {
    this.dispatch(verifyOtp());

    const { data, error } = await verifyMobileOtpRequest(mobile, otp, sessionId);
    if (data) {
      loginSuccess(data?.data, "phone_otp", "");
      this.dispatch(
        updateUserData({
          status: true,
          data: data?.data,
        }),
      );
      this.dispatch(
        openToast({
          title: "Login success",
          message: "You are successfully logged in",
        }),
      );
      this.dispatch(verifyOtpSuccess());
    } else {
      this.dispatch(verifyOtpError(error));
    }
  }
}
export default LoginDispatcher;
